import React, {ChangeEvent, useCallback, useContext, useEffect, useRef, useState} from 'react';
import {Flex, Input, Text} from '@priority-software/priority-style-react';
import {AutomationActionToolbar} from '../../AutomationActionToolbar';
import {SelectControl} from '../../AutomationRuleSelect/SelectControl';
import {IAlert} from '../../../../../actions/proto';
import {BusinessRuleAction, ruleDialogID} from '../../../../../constants';
import {AutomationChoose} from '../../../../../reducer/model';
import {MultiSelectValues} from './parts/MultiSelectValues/MultiSelectValues';
import {GridContainer} from '../../../../common/GridLayout/GridLayout';
import {AlertFields, AlertMessageFields} from '../../../../../enums';
import {AutofillTarget, RecipientsType} from './enum';
import {EmailField} from './parts/EmailField/EmailField';
import {EmailValues} from './parts/EmailValues/EmailValues';
import {AttachmentSection} from './parts/Attachments/AttachmentSection';
import {AutomationContext} from '../../../../../context';
import {useBusinessRuleTemplatesData} from './reducer';
import {texts} from '../../../../../texts';
import {texts as BPMtext} from '../../../../../modules/BPMRules/texts';
import {
    getSupportedRecipients,
    isMessageTemplate,
    prepareFieldOptions,
    shouldShowTabs
} from './utils';
import {StatusDropdown} from './parts/StatusDropdown';
import {DropdownItemData} from '@priority-software/priority-style-react/dist/components/DropdownV2/ChooseDropdown/ChooseDropdown';
import {createDataTestId} from '../../../../../../../utils/dataTestId';
import {getBpmStatusesAsChooseList} from '../../../../../../../_shared/GWTBridge/invoke/bpm/bpmMain';
import styles from './styles.module.scss';
import {ToggleButton} from '../../../../../../../components/ToggleButton';
import {CheckedValue} from '../../../../../../../components/ToggleButton/enum';
import {useNewTemplate} from './hooks/useNewTemplate';
import {RuleDialogContext} from '../../../../RulesListDialog/context';
import {SUBJECT_MAX_LENGTH} from '../../../../../../EmailEditor/constants';
import {AIContentAssistant} from './parts/AIContentAssistant';

type Props = {
    supportSubject?: boolean;
    supportMessage?: boolean;
    supportAttachment?: boolean;
    supportRecipients?: boolean;
    supportWebhooks?: boolean;
    supportStatuses?: boolean;
    supportHTMLEditor?: boolean;
    activeAction?: number;
    onRuleUpdate: (data: Partial<IAlert>) => void;
};

export const BusinessRuleActionTemplate = (props: Props) => {
    const {
        supportSubject,
        supportAttachment,
        supportWebhooks,
        supportStatuses,
        supportRecipients,
        supportMessage,
        supportHTMLEditor,
        activeAction,
        onRuleUpdate
    } = props;

    const {
        choosesOptions,
        openRule,
        automationType,
        toStatus,
        emailTemplateFields,
        getEmailTemplateFields,
        newEditorSupport: isNewEmailAllow
    } = useContext(AutomationContext);
    const {toggleHasTemporaryEmailTemplate} = useContext(RuleDialogContext);

    const {
        Fields,
        EnglishFields,
        Webhooks: webhooksOptions,
        MailTypes,
        User,
        Groups,
        MailFields,
        Documents
    } = choosesOptions;
    const [Statuses] = useState(getBpmStatusesAsChooseList(toStatus));

    const [conditionOperator, setConditionOperator] = useState<CheckedValue>(CheckedValue.left);

    const {state, handlers} = useBusinessRuleTemplatesData({openRule, onRuleUpdate});
    const {currentMessageData, Messages, CoverLetters} = state;
    const [columnsOptions] = useState(prepareFieldOptions(Fields, EnglishFields));

    const {
        onMessageLangChange,
        handleMessageData,
        insertShortcodeData,
        updateRuleField,
        addEmailValues,
        changeStatus,
        removeEmailValue,
        changeCoverLetter
    } = handlers;
    const messageCursorLastPosition = useRef<number>(currentMessageData?.Message?.length || 0);
    const subjectInputRef = useRef<HTMLInputElement>();
    const messageTextareaRef = useRef<HTMLTextAreaElement>();
    const autofillTarget = useRef(AutofillTarget.Message);
    const [recipientTypeOptions, setRecipientTypeOptions] = useState([]);
    const recipientType = state.MailType;

    const showOldTemplate = !!openRule?.AlertNumber && isMessageTemplate(Messages);
    const isDisabledOldTemplate = isNewEmailAllow && supportHTMLEditor && showOldTemplate;

    const shouldShowLangTabs = shouldShowTabs(Messages, automationType);

    const {renderNewTemplate} = useNewTemplate({
        isDisabledOldTemplate,
        Messages,
        CoverLetters,
        changeCoverLetter,
        toggleHasTemporaryEmailTemplate,
        dialogTitle: openRule.AlertNumber ? openRule.AlertNumber.toString() : texts.new_label,
        dialogDescription: openRule.Description,
        emailTemplateFields,
        getEmailTemplateFields
    });

    useEffect(() => {
        if (activeAction) {
            const recipientTypes = activeAction
                ? MailTypes.filter(({value}) => {
                      return (
                          getSupportedRecipients(automationType, activeAction).indexOf(
                              value as RecipientsType
                          ) >= 0
                      );
                  })
                : [];
            const firstOption = recipientTypes[0];

            setRecipientTypeOptions(recipientTypes);
            if (firstOption && !recipientType) {
                updateRuleField(firstOption.value, AlertFields.MailType);
            }
        }
    }, [activeAction, updateRuleField, MailTypes, recipientType, automationType]);

    useEffect(() => {
        const textareaEl = messageTextareaRef.current;
        const subjectEl = subjectInputRef.current;

        function handleCursorPosition(e) {
            messageCursorLastPosition.current = e.target.selectionEnd;
        }

        if (textareaEl) {
            // Message handlers Setup
            textareaEl.addEventListener('click', handleCursorPosition);
            textareaEl.addEventListener('keyup', handleCursorPosition);
        }

        if (subjectEl) {
            // Subject handlers Setup
            subjectEl.addEventListener('click', handleCursorPosition);
            subjectEl.addEventListener('keyup', handleCursorPosition);
        }
        return () => {
            // Message handlers CleanUp
            if (textareaEl) {
                textareaEl.removeEventListener('click', handleCursorPosition);
                textareaEl.removeEventListener('keyup', handleCursorPosition);
            }

            // Subject handlers CleanUp
            if (subjectEl) {
                subjectEl.removeEventListener('click', handleCursorPosition);
                subjectEl.removeEventListener('keyup', handleCursorPosition);
            }
        };
    }, []);

    const handleMessageValue = useCallback(
        (e: ChangeEvent<HTMLTextAreaElement>) => {
            const {name, value} = e.target;
            handleMessageData(name, value);
            messageCursorLastPosition.current = e.target.selectionEnd;
        },
        [handleMessageData]
    );

    const handleSubject = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const {name, value} = e.target;
            handleMessageData(name, value);
        },
        [handleMessageData]
    );

    const handleShortcodeData = useCallback(
        (text: string) => {
            const shortcode = `<<${text}>>`;
            const targetControlRef =
                autofillTarget.current === AutofillTarget.Subject
                    ? subjectInputRef
                    : messageTextareaRef;

            const message = targetControlRef.current.value;
            const position = messageCursorLastPosition.current;

            messageCursorLastPosition.current =
                messageCursorLastPosition.current + shortcode.length;

            insertShortcodeData(
                message.slice(0, position) + shortcode + message.slice(position, message.length),
                autofillTarget.current
            );
            targetControlRef.current.focus();
        },
        [insertShortcodeData]
    );

    const handleMessageCursorPosition = useCallback(e => {
        messageCursorLastPosition.current = e.target.selectionEnd;
    }, []);

    const commonOptionHandler = useCallback(
        (option: AutomationChoose, name: string) => {
            updateRuleField(option.value, name);
        },
        [updateRuleField]
    );

    const handleRecipientPhone = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            updateRuleField([e.target.value], AlertFields.RecipientMail);
        },
        [updateRuleField]
    );

    const onMultiValueChange = useCallback(
        (value: Array<any>, name: string) => {
            updateRuleField(value, name);
        },
        [updateRuleField]
    );

    const onSingleRecipientValueChange = useCallback(
        (option: AutomationChoose, name: string) => {
            /*
             * According to the product request we should support multi-recipients only for `Send e-mail` action type.
             * For the rest actions with a recipients support we should handle as a single value.
             * But BE expects these value in the array type (EP-28189)
             */
            updateRuleField([option.value as string], name);
        },
        [updateRuleField]
    );

    const getRecipientData = () => {
        const isMulti = activeAction === BusinessRuleAction.EmailMsg;

        switch (recipientType) {
            case RecipientsType.user:
                return {
                    name: AlertFields.RecipientUser,
                    options: User,
                    defaultValue: isMulti
                        ? (state.RecipientUser as Array<number>)
                        : (state.RecipientUser[0] as number),
                    onMultiChange: onMultiValueChange,
                    onChange: onSingleRecipientValueChange,
                    isMulti
                };
            case RecipientsType.group:
                return {
                    name: AlertFields.RecipientGroup,
                    options: Groups,
                    defaultValue: isMulti
                        ? (state.RecipientGroup as Array<number>)
                        : (state.RecipientGroup[0] as number),
                    onMultiChange: onMultiValueChange,
                    onChange: onSingleRecipientValueChange,
                    isMulti
                };
            case RecipientsType.field:
                return {
                    name: AlertFields.RecipientField,
                    options: MailFields,
                    defaultValue: state.RecipientField as number,
                    onChange: commonOptionHandler
                };
            default:
                return null;
        }
    };

    const recipientData = getRecipientData();

    const renderRecipientField = () => {
        if (recipientType === RecipientsType.email) {
            return <EmailField onSubmit={addEmailValues} />;
        } else if (recipientType === RecipientsType.phone) {
            return (
                <Input
                    value={state.RecipientMail[0]}
                    onChange={handleRecipientPhone}
                    size="medium"
                />
            );
        } else if (recipientData) {
            return <SelectControl {...recipientData} menuScrollMaxHeight={300} />;
        }

        return null;
    };

    const onStatusChange = useCallback(
        (option: DropdownItemData, name: string) => {
            changeStatus(option.value as number, name);
        },
        [changeStatus]
    );

    const getSendToLabel = () => {
        if (activeAction === BusinessRuleAction.AllowStatusChange) {
            return BPMtext.allow_status_change_for;
        } else if (
            activeAction === BusinessRuleAction.OpenTask ||
            activeAction === BusinessRuleAction.AssignTo
        ) {
            return texts.assign_to_label;
        }
        return texts.send_to_label;
    };

    const renderAttachments = () =>
        supportAttachment && Documents.length > 0 ? (
            <AttachmentSection
                document={state.Document as number}
                documentFormat={state.DocumentFormat as number}
                documentOptions={Documents}
                fromAttachment={state.ExtFile}
                isPDF={state.Pdf}
                isWord={state.WordTemplates}
                updateRuleField={updateRuleField}
            />
        ) : null;

    const renderOldTemplate = () => (
        <>
            {supportMessage ? (
                <AutomationActionToolbar
                    activeTabId={state.activeMessageLang}
                    columnsOptions={columnsOptions[state.activeMessageLang] || []}
                    onTabChange={onMessageLangChange}
                    handleMessageData={handleShortcodeData}
                    isDisabledOldTemplate={isDisabledOldTemplate}
                    shouldShowTabs={shouldShowLangTabs}
                />
            ) : null}

            <div className={styles.textFieldsWrap}>
                {supportSubject ? (
                    <div {...createDataTestId('subject-section', false)}>
                        <Text tag="label" skin="paragraph-2" textColor="primary-700">
                            {texts.input_subject_label}
                        </Text>
                        <Input
                            readOnly={isDisabledOldTemplate}
                            disabled={isDisabledOldTemplate}
                            inputRef={subjectInputRef}
                            key={state.activeMessageLang}
                            name={AlertMessageFields.Subject}
                            skin="box"
                            value={currentMessageData?.Subject || ''}
                            placeholder={texts.input_subject_placeholder}
                            onChange={handleSubject}
                            maxLength={
                                BusinessRuleAction.EmailMsg === activeAction
                                    ? SUBJECT_MAX_LENGTH
                                    : undefined
                            }
                            onFocus={() => (autofillTarget.current = AutofillTarget.Subject)}
                        />
                    </div>
                ) : null}
                {supportMessage ? (
                    <div {...createDataTestId('message-section', false)}>
                        <Flex alignItems="flex-end" style={{marginBottom: 1, minHeight: 32}}>
                            <Text
                                tag="label"
                                skin="paragraph-2"
                                textColor="primary-700"
                                componentClasses="pui-label-required"
                            >
                                {texts.input_message_label}
                            </Text>

                            {currentMessageData?.Message && !isDisabledOldTemplate && (
                                <AIContentAssistant
                                    handleMessageData={handleMessageData}
                                    currentMessage={currentMessageData.Message}
                                    messageLang={Number(currentMessageData.Language)}
                                />
                            )}
                        </Flex>

                        <textarea
                            readOnly={isDisabledOldTemplate}
                            disabled={isDisabledOldTemplate}
                            key={state.activeMessageLang}
                            name={AlertMessageFields.Message}
                            ref={messageTextareaRef}
                            onBlur={handleMessageCursorPosition}
                            className={styles.textarea}
                            value={state.currentMessageData?.Message || ''}
                            onChange={handleMessageValue}
                            placeholder={texts.input_message_placeholder}
                            onFocus={() => (autofillTarget.current = AutofillTarget.Message)}
                        />
                    </div>
                ) : null}
                {renderAttachments()}
            </div>
        </>
    );

    return (
        <div className={styles.component}>
            {supportWebhooks && (
                <div className={styles.recipientSection}>
                    <div className={styles.row}>
                        <div className={styles.rowItem}>
                            <Text
                                tag="label"
                                skin="paragraph-2"
                                textColor="primary-700"
                                componentClasses="pui-label-required"
                            >
                                {texts.send_to_label}
                            </Text>
                            <SelectControl
                                name={AlertFields.WebHook}
                                defaultValue={state.WebHook || undefined} // should be `undefined` to allow the trigger to show placeholder text
                                onChange={commonOptionHandler}
                                options={webhooksOptions}
                                menuScrollMaxHeight={300}
                            />
                        </div>
                        <div className={styles.rowItem} />
                    </div>
                </div>
            )}

            {supportStatuses && (
                <div>
                    <StatusDropdown
                        name="ToStatus"
                        onChange={onStatusChange}
                        value={state.ToStatus as number}
                        options={Statuses}
                        portalSelector={ruleDialogID}
                        placeholder={texts.select_placeholder}
                    />
                </div>
            )}

            {supportRecipients && (
                <div
                    className={styles.recipientSection}
                    {...createDataTestId('recipient-section', false)}
                >
                    <GridContainer columnCount={2}>
                        <div
                            className={styles.rowItem}
                            {...createDataTestId('send-to-type-block', false)}
                        >
                            <Text
                                tag="label"
                                skin="paragraph-2"
                                textColor="primary-700"
                                componentClasses="pui-label-required"
                            >
                                {getSendToLabel()}
                            </Text>

                            <SelectControl
                                name={AlertFields.MailType}
                                defaultValue={recipientType}
                                onChange={commonOptionHandler}
                                options={recipientTypeOptions}
                                menuScrollMaxHeight={300}
                            />
                        </div>

                        <div
                            className={styles.rowItem}
                            {...createDataTestId('send-to-specific-block', false)}
                        >
                            {renderRecipientField()}
                        </div>
                    </GridContainer>

                    <div className={styles.row}>
                        <div className={styles.rowItem}>
                            {recipientData &&
                                recipientData.isMulti &&
                                Array.isArray(recipientData.defaultValue) && (
                                    <MultiSelectValues
                                        values={recipientData.defaultValue}
                                        options={recipientData.options}
                                        name={recipientData.name}
                                        onChange={onMultiValueChange}
                                    />
                                )}

                            {activeAction === BusinessRuleAction.EmailMsg &&
                            state.RecipientMail.length > 0 ? (
                                <EmailValues
                                    data={state.RecipientMail}
                                    onRemove={removeEmailValue}
                                />
                            ) : null}
                            {isDisabledOldTemplate && (
                                <div className={styles.toggleButton}>
                                    <ToggleButton
                                        {...createDataTestId(`email-toggle-button`, true)}
                                        size="small"
                                        checked={conditionOperator}
                                        onChange={check =>
                                            setConditionOperator(
                                                check === CheckedValue.left
                                                    ? CheckedValue.left
                                                    : CheckedValue.right
                                            )
                                        }
                                        left={texts.old_email_template_label}
                                        right={texts.new_email_template_label}
                                        itemWidth={'100px'}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {supportHTMLEditor && isNewEmailAllow
                ? conditionOperator === CheckedValue.left && showOldTemplate
                    ? renderOldTemplate()
                    : renderNewTemplate(renderAttachments)
                : renderOldTemplate()}
        </div>
    );
};
